import { use, useState } from "react";
import clsx from "clsx";

import { getConfig } from "lib/get-config";
import { useTranslation } from "lib/i18n/use-translation";
import { ModalsContext } from "lib/modals/use-modals";
import { NotificationsContext } from "lib/notifications/use-notifications";
import { useQuotes } from "lib/quotes/use-quotes";
import { fetchBookPath } from "lib/urls/book-path";

import CloseIcon from "assets/icons/close24.svg?react";
import LinkIcon from "assets/icons/link.svg?react";
import VkIcon from "assets/icons/vk.svg?react";
import css from "./share-quote.module.scss";

export function ShareQuote() {
  const { shareQuote, closeShareQuote } = use(ModalsContext)!;
  const { publishQuote } = useQuotes();
  const { t } = useTranslation();
  const { showToast } = use(NotificationsContext)!;
  const { art } = getConfig();

  const [isSubmitting, setIsSubmitting] = useState(false);

  async function getQuoteUrl(): Promise<string | null> {
    if (!shareQuote.quoteId || isSubmitting) return null;

    setIsSubmitting(true);

    try {
      const [, publishError] = await publishQuote(shareQuote.quoteId);
      if (publishError) {
        showToast(t("Не удалось опубликовать цитату"), {
          message: t("Пожалуйста, обновите страницу и попробуйте снова"),
        });
        return null;
      }

      const bookPath = await fetchBookPath(art);
      return `${import.meta.env.VITE_LITRES_URL}${bookPath}/citata/${shareQuote.quoteId}/`;
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleVkShare() {
    const url = await getQuoteUrl();
    if (!url) return;

    const text = `\u00AB${shareQuote.quoteText.trim()}\u00BB`;
    const vkShareUrl = `https://vk.com/share.php?url=${encodeURIComponent(url)}&comment=${encodeURIComponent(text)}`;

    window.open(vkShareUrl, "vk_share", "width=550,height=450");
    closeShareQuote();
  }

  async function handleCopyLink() {
    const url = await getQuoteUrl();
    if (!url) return;

    await navigator.clipboard.writeText(url);
    showToast(t("Ссылка скопирована"), { variant: "info" });
    closeShareQuote();
  }

  return (
    <div className={clsx(css.overlay, { [css.open]: shareQuote.isOpen })}>
      <div className={css.modal}>
        <button className={css.closeButton} onClick={closeShareQuote}>
          <CloseIcon />
        </button>

        <div className={css.header}>
          <h2 className={css.title}>{t("Поделиться")}</h2>
          <p className={css.subtitle}>{t("Выберите способ")}</p>
        </div>

        <div className={css.options}>
          <div className={css.socialIcons}>
            <button
              className={css.socialButton}
              onClick={handleVkShare}
              disabled={isSubmitting}
              title={t("ВКонтакте")}
            >
              <VkIcon />
            </button>
          </div>

          <button
            className={css.copyLinkButton}
            onClick={handleCopyLink}
            disabled={isSubmitting}
          >
            <LinkIcon />
            <span>{t("Скопировать ссылку")}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
